import * as React from 'react'
import { Controller, ControllerProps, FieldValues } from 'react-hook-form'
import { NumericFormat, NumericFormatProps } from 'react-number-format'
import { InlineInput } from './InlineInput'
import { InlineInputProps } from './types'

export type InlineCurrencyInputProps = Omit<NumericFormatProps, 'type' | 'size' | 'format'> & InlineInputProps
/**
 * Base component for constructing inputs that follow particular formats - in general,
 * you should not need to render this component directly, but instead provide a wrapper
 * that 'standardizes' the mask for things like currency, phone numbers, etc.
 */
const _InlineCurrencyInput = (
  { onChange, name, ...props }: InlineCurrencyInputProps,
  ref: React.Ref<HTMLInputElement>
) => (
  <NumericFormat
    getInputRef={ref}
    prefix="$"
    valueIsNumericString
    allowNegative={false}
    decimalScale={2}
    thousandSeparator
    // InlineInput's `color` props is a subset of string
    // @ts-expect-error the customInput prop is broadening the `color` type to `string`
    customInput={InlineInput}
    onValueChange={({ value: v }, { event }) =>
      onChange &&
      onChange({
        ...event,
        target: { name, value: v } as unknown as HTMLInputElement,
      } as React.ChangeEvent<HTMLInputElement>)
    }
    {...props}
    type="text"
  />
)

export const InlineCurrencyInput = React.forwardRef(_InlineCurrencyInput)

type InlineCurrencyInputControllerProps<T extends FieldValues> = InlineCurrencyInputProps &
  Pick<ControllerProps<T>, 'name' | 'control' | 'rules'>
export const InlineCurrencyInputController = <T extends FieldValues>({
  name,
  control,
  rules,
  ...props
}: InlineCurrencyInputControllerProps<T>) => (
  <Controller
    render={({ field, formState: { submitCount }, fieldState: { error, isTouched } }) => {
      const isError = Boolean((isTouched || submitCount > 0) && error?.message)
      return (
        <InlineCurrencyInput {...props} {...field} error={isError} helperText={isError && error?.message} />
      )
    }}
    name={name}
    control={control}
    rules={rules}
  />
)
